import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  BadgeCheck,
  Briefcase,
  CheckCircle2,
  Eye,
  FileText,
  GraduationCap,
  Loader2,
  Mail,
  MapPin,
  PauseCircle,
  PlayCircle,
  Search,
  ShieldCheck,
  UserRound,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { adminAPI } from '../../services/api';

const inputClass = 'h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
const textareaClass = 'min-h-[96px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

const accountOptions = [
  { value: 'all', label: 'All accounts' },
  { value: 'active', label: 'Active' },
  { value: 'pending_verification', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' }
];

const visibilityOptions = [
  { value: 'all', label: 'All visibility' },
  { value: 'live', label: 'Live' },
  { value: 'paused', label: 'Paused' },
  { value: 'hidden', label: 'Hidden' }
];

const availabilityOptions = [
  { value: 'all', label: 'All availability' },
  { value: 'immediately', label: 'Immediately' },
  { value: 'two_weeks', label: 'Two weeks' },
  { value: 'one_month', label: 'One month' },
  { value: 'three_months', label: 'Three months' },
  { value: 'not_looking', label: 'Not looking' }
];

const readinessOptions = [
  { value: 'all', label: 'All readiness' },
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' }
];

const sortOptions = [
  { value: 'created_at', label: 'Newest' },
  { value: 'updated_at', label: 'Recently updated' },
  { value: 'last_login_at', label: 'Last login' },
  { value: 'profile_completion', label: 'Profile readiness' },
  { value: 'matches', label: 'Matches' },
  { value: 'skills', label: 'Skills' }
];

const labelize = (value) => String(value || 'Not set')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDate = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'live', 'complete', 'completed', 'verified'].includes(normalized)) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (['pending', 'pending_verification', 'paused', 'two_weeks', 'one_month', 'three_months'].includes(normalized)) return 'border-amber-100 bg-amber-50 text-amber-700';
  if (['suspended', 'hidden', 'inactive', 'not_looking', 'failed'].includes(normalized)) return 'border-rose-100 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const initialsFor = (name) => String(name || 'Candidate')
  .split(' ')
  .filter(Boolean)
  .map((part) => part[0])
  .join('')
  .slice(0, 2)
  .toUpperCase();

const Badge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(status)}`}>
    {labelize(status)}
  </span>
);

const StatCard = ({ icon: Icon, label, value, detail }) => (
  <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-semibold text-slate-600">{label}</h3>
      <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-blue-600 border border-slate-100/50">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {detail && <p className="text-xs font-medium text-slate-400 mt-2">{detail}</p>}
    </div>
  </div>
);

function DetailItem({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-medium text-slate-800">{value || 'Not set'}</p>
    </div>
  );
}

const CandidatesSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-28 rounded-md" />
        <SkeletonBlock className="h-9 w-72 rounded-lg" />
        <SkeletonBlock className="h-4 w-full max-w-2xl rounded-md" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-28 rounded-[24px]" />)}
      </div>
      <SkeletonBlock className="h-[540px] rounded-[24px]" />
    </div>
  </div>
);

function CandidateDetailsModal({ candidate, onClose, onAction }) {
  if (!candidate) return null;
  const currentRole = candidate.experienceLevel || (candidate.totalExperienceYears ? `${candidate.totalExperienceYears} years experience` : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <section className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Candidate profile</p>
            <h2 className="mt-2 truncate text-2xl font-semibold tracking-tight text-slate-950">{candidate.name}</h2>
            <p className="mt-1 text-sm text-slate-500">{candidate.email}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DetailItem label="Account" value={labelize(candidate.accountStatus)} />
            <DetailItem label="Visibility" value={labelize(candidate.visibilityStatus)} />
            <DetailItem label="Readiness" value={`${Math.round(candidate.profileCompletionPercentage || 0)}%`} />
            <DetailItem label="Matches" value={formatNumber(candidate.stats?.matches)} />
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-5">
              <section className="rounded-[24px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">Profile</h3>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-2">
                  <DetailItem label="Location" value={candidate.currentLocation} />
                  <DetailItem label="Nationality" value={candidate.nationality} />
                  <DetailItem label="Availability" value={labelize(candidate.availabilityStatus)} />
                  <DetailItem label="Work type" value={labelize(candidate.preferredWorkType)} />
                  <DetailItem label="Experience" value={currentRole} />
                  <DetailItem label="Relocation" value={candidate.willingToRelocate ? 'Willing to relocate' : 'Not marked'} />
                  <DetailItem label="Expected salary" value={candidate.expectedSalaryRange} />
                  <DetailItem label="Current salary" value={candidate.currentSalaryRange} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">Evidence and hiring activity</h3>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-2">
                  <DetailItem label="Skills" value={`${formatNumber(candidate.stats?.skills)} total, ${formatNumber(candidate.stats?.primarySkills)} primary`} />
                  <DetailItem label="Experience records" value={formatNumber(candidate.stats?.experience)} />
                  <DetailItem label="Education records" value={formatNumber(candidate.stats?.education)} />
                  <DetailItem label="CVs" value={`${formatNumber(candidate.stats?.cvs)} uploaded, ${formatNumber(candidate.stats?.completedCvs)} completed`} />
                  <DetailItem label="Interviews" value={`${formatNumber(candidate.stats?.interviews)} total, ${formatNumber(candidate.stats?.upcomingInterviews)} upcoming`} />
                  <DetailItem label="Assessments" value={formatNumber(candidate.stats?.assessments)} />
                  <DetailItem label="Offers" value={`${formatNumber(candidate.stats?.offers)} total, ${formatNumber(candidate.stats?.openOffers)} open`} />
                  <DetailItem label="Profile nudges" value={formatNumber(candidate.stats?.profileNudges)} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">Summary</h3>
                </div>
                <div className="space-y-3 p-4">
                  <DetailItem label="AI profile summary" value={candidate.aiProfileSummary} />
                  <DetailItem label="Personal statement" value={candidate.personalStatement} />
                  <DetailItem label="Portfolio" value={Array.isArray(candidate.portfolioUrls) && candidate.portfolioUrls.length ? candidate.portfolioUrls.join(', ') : null} />
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-950">Contact</h3>
                <div className="mt-3 space-y-3">
                  <DetailItem label="Email" value={candidate.email} />
                  <DetailItem label="Phone" value={candidate.phone} />
                  <DetailItem label="Email verified" value={formatDateTime(candidate.emailVerifiedAt)} />
                  <DetailItem label="Phone verified" value={formatDateTime(candidate.phoneVerifiedAt)} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-950">CV and activity</h3>
                <div className="mt-3 space-y-3">
                  <DetailItem label="Primary CV" value={candidate.primaryCv?.fileName} />
                  <DetailItem label="CV status" value={labelize(candidate.primaryCv?.status)} />
                  <DetailItem label="Last login" value={formatDateTime(candidate.lastLoginAt)} />
                  <DetailItem label="Last activity" value={formatDateTime(candidate.latest?.activityAt)} />
                </div>
              </section>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onAction(candidate, 'suspended')}
                  disabled={candidate.accountStatus === 'suspended'}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PauseCircle className="h-4 w-4" />
                  Suspend
                </button>
                <button
                  type="button"
                  onClick={() => onAction(candidate, 'active')}
                  disabled={candidate.accountStatus !== 'suspended'}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlayCircle className="h-4 w-4" />
                  Reactivate
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function AccountActionModal({ action, onClose, onSubmit, saving }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    setReason('');
  }, [action?.candidate?.id, action?.status]);

  if (!action) return null;
  const suspending = action.status === 'suspended';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <section className="w-full max-w-lg overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="border-b border-slate-100 p-5">
          <h2 className="text-lg font-semibold text-slate-950">
            {suspending ? 'Suspend candidate account' : 'Reactivate candidate account'}
          </h2>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            {suspending
              ? 'This revokes active refresh tokens for the candidate account.'
              : 'This restores access for a candidate account that is currently suspended.'}
          </p>
        </div>
        <div className="p-5">
          <p className="mb-3 text-sm font-medium text-slate-700">{action.candidate?.name}</p>
          <label className="block text-sm font-medium text-slate-700">
            Reason{suspending ? '' : ' (optional)'}
          </label>
          <textarea
            className={`${textareaClass} mt-2`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={suspending ? 'Record the reason for suspension.' : 'Record why access is being restored.'}
          />
          {suspending && reason.trim().length > 0 && reason.trim().length < 10 && (
            <p className="mt-2 text-xs font-medium text-rose-600">Use at least 10 characters.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={saving || (suspending && reason.trim().length < 10)}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {suspending ? 'Suspend' : 'Reactivate'}
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white px-6 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-500">
        <UserRound className="h-6 w-6" />
      </div>
      <h2 className="text-lg font-semibold text-slate-950">No candidates found</h2>
      <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
        Adjust the search or filters to view candidate accounts and hiring readiness.
      </p>
      <button type="button" onClick={onReset} className="mt-5 inline-flex h-10 items-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-blue-600">
        Reset filters
      </button>
    </div>
  );
}

function CandidatesListPage() {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({ accountStatuses: [], visibilityStatuses: [], readiness: {} });
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    accountStatus: 'all',
    visibilityStatus: 'all',
    availabilityStatus: 'all',
    readiness: 'all',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [accountAction, setAccountAction] = useState(null);
  const [savingAction, setSavingAction] = useState(false);

  const loadCandidates = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await adminAPI.getCandidates({
        ...filters,
        limit: pagination.limit,
        offset
      });
      setCandidates(response.candidates || []);
      setStats(response.stats || { accountStatuses: [], visibilityStatuses: [], readiness: {} });
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Candidates could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadCandidates({ offset: 0 });
  }, [loadCandidates]);

  const totals = useMemo(() => {
    const active = stats.accountStatuses?.find((item) => item.status === 'active')?.count || 0;
    const suspended = stats.accountStatuses?.find((item) => item.status === 'suspended')?.count || 0;
    const live = stats.visibilityStatuses?.find((item) => item.status === 'live')?.count || 0;
    return {
      total: stats.total || pagination.total,
      active,
      suspended,
      live,
      complete: stats.readiness?.complete || 0,
      averageCompletion: stats.readiness?.averageCompletion || 0
    };
  }, [pagination.total, stats]);

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      accountStatus: 'all',
      visibilityStatus: 'all',
      availabilityStatus: 'all',
      readiness: 'all',
      sortBy: 'created_at',
      sortOrder: 'DESC'
    });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const nextSearch = searchInput.trim();
    if (nextSearch === filters.search) {
      loadCandidates({ offset: 0 });
      return;
    }
    setFilters((current) => ({ ...current, search: nextSearch }));
  };

  const updateAccountStatus = async (reason) => {
    if (!accountAction) return;
    setSavingAction(true);

    try {
      await adminAPI.updateCandidateAccountStatus(accountAction.candidate.id, {
        status: accountAction.status,
        reason: reason.trim()
      });
      toast.success(accountAction.status === 'suspended' ? 'Candidate suspended.' : 'Candidate reactivated.');
      setAccountAction(null);
      setSelectedCandidate(null);
      await loadCandidates({ offset: pagination.offset, quiet: true });
    } catch (actionError) {
      toast.error(extractError(actionError, 'Account status could not be updated.'));
    } finally {
      setSavingAction(false);
    }
  };

  if (loading) return <CandidatesSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Candidate list</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Account state, profile readiness, CV status, skills, experience, matches, interviews, assessments, offers, and recent activity.
            </p>
          </div>

        </div>

        {error && (
          <div className="rounded-[24px] border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={UserRound} label="Candidates" value={formatNumber(totals.total)} detail={`${formatNumber(totals.active)} active accounts`} />
          <StatCard icon={CheckCircle2} label="Live profiles" value={formatNumber(totals.live)} detail={`${formatNumber(totals.suspended)} suspended accounts`} />
          <StatCard icon={BadgeCheck} label="Complete profiles" value={formatNumber(totals.complete)} detail={`${Math.round(totals.averageCompletion)}% average readiness`} />
          <StatCard icon={Briefcase} label="Loaded rows" value={formatNumber(candidates.length)} detail={`${formatNumber(pagination.total)} matching filters`} />
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5 sm:p-6 bg-white">
            <form onSubmit={submitSearch} className="flex flex-col xl:flex-row gap-4">
              <label className="relative block flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${inputClass} w-full pl-11`}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search name, email, phone, location, nationality..."
                />
              </label>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0 overflow-x-auto pb-1 xl:pb-0">
                <select className={`${inputClass} min-w-[145px] shrink-0`} value={filters.accountStatus} onChange={(event) => setFilters((current) => ({ ...current, accountStatus: event.target.value }))}>
                  {accountOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[145px] shrink-0`} value={filters.visibilityStatus} onChange={(event) => setFilters((current) => ({ ...current, visibilityStatus: event.target.value }))}>
                  {visibilityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[145px] shrink-0`} value={filters.availabilityStatus} onChange={(event) => setFilters((current) => ({ ...current, availabilityStatus: event.target.value }))}>
                  {availabilityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[145px] shrink-0`} value={filters.readiness} onChange={(event) => setFilters((current) => ({ ...current, readiness: event.target.value }))}>
                  {readinessOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[145px] shrink-0`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                  {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button type="submit" className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white transition hover:bg-blue-600 shadow-sm shrink-0">
                  Search
                </button>
              </div>
            </form>
          </div>

          {!candidates.length ? (
            <div className="p-5">
              <EmptyState onReset={resetFilters} />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-[1360px] divide-y divide-slate-100">
                  <thead>
                    <tr className="bg-slate-50/50">
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Candidate</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Status</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Profile</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">CV</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Evidence</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Hiring</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Activity</th>
                      <th className="py-4 px-6 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {candidates.map((candidate) => (
                      <tr key={candidate.id} className="align-top hover:bg-slate-50/80 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4 min-w-[240px]">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold overflow-hidden shrink-0 border border-blue-100/50 shadow-sm">
                              {candidate.profilePhotoUrl ? <img src={candidate.profilePhotoUrl} alt="" className="w-full h-full object-cover" /> : initialsFor(candidate.name)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate">{candidate.name}</p>
                              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" /> {candidate.email}</p>
                              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {candidate.currentLocation || 'Location not set'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge status={candidate.accountStatus} />
                          <div className="mt-2"><Badge status={candidate.visibilityStatus} /></div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-slate-950">{Math.round(candidate.profileCompletionPercentage || 0)}% ready</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(candidate.availabilityStatus)} availability</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(candidate.preferredWorkType)} work</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="max-w-[180px] truncate text-sm font-semibold text-slate-950">{candidate.primaryCv?.fileName || 'No primary CV'}</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(candidate.primaryCv?.status)}</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(candidate.stats?.cvs)} uploaded</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-slate-950">{formatNumber(candidate.stats?.skills)} skills</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(candidate.stats?.experience)} work, {formatNumber(candidate.stats?.education)} education</p>
                          <p className="mt-1 text-xs text-slate-500">{candidate.totalExperienceYears ?? 0} years total</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-slate-950">{formatNumber(candidate.stats?.matches)} matches</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(candidate.stats?.interviews)} interviews, {formatNumber(candidate.stats?.assessments)} assessments</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(candidate.stats?.offers)} offers, {formatNumber(candidate.stats?.openOffers)} open</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{formatDate(candidate.createdAt)}</p>
                          <p className="mt-1 text-xs text-slate-500">Login: {formatDate(candidate.lastLoginAt, 'No login')}</p>
                          <p className="mt-1 text-xs text-slate-500">Activity: {formatDate(candidate.latest?.activityAt, 'No activity')}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setSelectedCandidate(candidate)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Profile">
                              <Eye className="w-4 h-4" />
                            </button>
                            {candidate.accountStatus === 'suspended' ? (
                              <button type="button" onClick={() => setAccountAction({ candidate, status: 'active' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors shadow-sm border border-emerald-100/50" title="Reactivate Account">
                                <PlayCircle className="w-4 h-4" />
                              </button>
                            ) : (
                              <button type="button" onClick={() => setAccountAction({ candidate, status: 'suspended' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors shadow-sm border border-rose-100/50" title="Suspend Account">
                                <PauseCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 xl:hidden">
                {candidates.map((candidate) => (
                  <article key={candidate.id} className="rounded-[24px] border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold overflow-hidden shrink-0 border border-blue-100/50 shadow-sm">
                          {candidate.profilePhotoUrl ? <img src={candidate.profilePhotoUrl} alt="" className="w-full h-full object-cover" /> : initialsFor(candidate.name)}
                        </div>
                        <div className="min-w-0 flex flex-col justify-center">
                          <h2 className="truncate font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{candidate.name}</h2>
                          <p className="mt-1 text-sm text-slate-500">{candidate.email}</p>
                        </div>
                      </div>
                      <Badge status={candidate.accountStatus} />
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {candidate.currentLocation || 'Location not set'}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><ShieldCheck className="h-4 w-4 text-slate-400" /> {labelize(candidate.visibilityStatus)}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><FileText className="h-4 w-4 text-slate-400" /> {candidate.primaryCv?.fileName || 'No primary CV'}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><GraduationCap className="h-4 w-4 text-slate-400" /> {formatNumber(candidate.stats?.education)} education records</p>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-lg font-bold text-slate-950">{Math.round(candidate.profileCompletionPercentage || 0)}%</p>
                        <p className="text-xs text-slate-500">Ready</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-lg font-bold text-slate-950">{formatNumber(candidate.stats?.skills)}</p>
                        <p className="text-xs text-slate-500">Skills</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-lg font-bold text-slate-950">{formatNumber(candidate.stats?.matches)}</p>
                        <p className="text-xs text-slate-500">Matches</p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                      <Badge status={candidate.visibilityStatus} />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setSelectedCandidate(candidate)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Profile">
                          <Eye className="w-4 h-4" />
                        </button>
                        {candidate.accountStatus === 'suspended' ? (
                          <button type="button" onClick={() => setAccountAction({ candidate, status: 'active' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors shadow-sm border border-emerald-100/50" title="Reactivate Account">
                            <PlayCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button type="button" onClick={() => setAccountAction({ candidate, status: 'suspended' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors shadow-sm border border-rose-100/50" title="Suspend Account">
                            <PauseCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between rounded-b-[24px]">
                <p className="text-sm text-slate-500">
                  Showing {formatNumber(pagination.offset + 1)} - {formatNumber(pagination.offset + candidates.length)} of {formatNumber(pagination.total)}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.offset <= 0 || refreshing}
                    onClick={() => loadCandidates({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })}
                    className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!pagination.hasMore || refreshing}
                    onClick={() => loadCandidates({ offset: pagination.offset + pagination.limit, quiet: true })}
                    className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <CandidateDetailsModal
        candidate={selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        onAction={(candidate, status) => setAccountAction({ candidate, status })}
      />
      <AccountActionModal
        action={accountAction}
        onClose={() => setAccountAction(null)}
        onSubmit={updateAccountStatus}
        saving={savingAction}
      />
    </div>
  );
}

export default CandidatesListPage;
